/**
 * Apollo NavigationBar shell for the app header.
 * Replaces spark-ui/HeaderBar + AdminMenu/ReportMenu/SafetyLetterMenu.
 * Redux connect() retains enterprise/role navigation dispatch logic from the legacy header;
 * hooks (useSparkHeaderMenu, useSparkHeaderProfile) build Apollo menu props from Redux state.
 */
import React from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import NavigationBar from 'apollo-react/components/NavigationBar';
import {
    setCurrentEnterprise,
    setUserInfo,
    currentUserInfo,
    setDomainValue,
    userCurrentEnterprise,
    isManagementUser,
    isRecipient,
    isSupportUser,
    isGapPacketAdmin,
    isIBManagementUser,
    setCurrentEnterpriseRole,
    getCurrentActiveRole,
    getReportEnabledForSponsor,
    getReportEnabledForCRO,
    setReportEnabledForSponsor,
    setReportEnabledForCRO,
} from 'spark-state/common/user/userReducer';
import { logOut, getMappedEnterpriseData } from 'spark-state/common/app/reducer';
import { refreshRecipient, refreshManagement } from 'spark-grid';
import { useHistory, useLocation } from 'react-router-dom';
import { reset, change } from 'redux-form';
import Roles from 'spark-types/Roles';
import { getEnterpriseCtmsRefreshDate } from 'spark-api/enterpriseService';
import { setCtmsRefreshDate } from 'spark-state/enterprise/enterpriseReducer';
import { setSafetyNotifications } from 'spark-state/safetyLetter/safetyLetterReducer';
import { trackUser } from 'spark-api/utilityService';
import { NAVIGATION_BAR_CONFIG } from './navigationBarConfig';
import SparkHeaderLogo from './SparkHeaderLogo';
import { useSparkHeaderMenu } from './useSparkHeaderMenu';
import { useSparkHeaderProfile } from './useSparkHeaderProfile';
import EnterpriseSelect from './EnterpriseSelect';
import SwitchRoleMenu from './SwitchRoleMenu';

const RightButtons = styled.div`
    display: flex;
    align-items: center;
`;

function SparkHeader({
    getMappedEnterprises,
    handleRoleSwitch,
    mappedEnterprise,
    isManagementUser: isManagementUserProp,
    isGapPacketAdmin: isGapPacketAdminProp,
    isIBManagementUser: isIBManagementUserProp,
    isRecipient: isRecipientProp,
    userInfo,
    isSupportUser,
    enterpriseId,
    availableRoles,
    reportEnabled,
    resetReportType,
    currentActiveRole,
    logOut: logOutAction,
}) {
    const history = useHistory();
    const location = useLocation();
    // SAF-18432: hide enterprise/role controls on User Guide page (legacy HeaderBar behavior).
    const isUserGuidePage = location.pathname.includes('/userGuides');

    const { menuItems, onNavClick, checkIsActive } = useSparkHeaderMenu({
        enterpriseId,
        reportEnabled,
        resetReportType,
        history,
    });

    const profileMenuProps = useSparkHeaderProfile({ logOut: logOutAction });

    // Same role gates as legacy EnterpriseDropdown in SparkHeader.
    const showEnterpriseSelect =
        !isUserGuidePage &&
        mappedEnterprise &&
        (isManagementUserProp ||
            isRecipientProp ||
            isSupportUser ||
            isGapPacketAdminProp ||
            isIBManagementUserProp);

    return (
        <NavigationBar
            {...NAVIGATION_BAR_CONFIG}
            LogoComponent={SparkHeaderLogo}
            menuItems={menuItems}
            profileMenuProps={profileMenuProps}
            otherButtons={
                <RightButtons>
                    {!isUserGuidePage ? (
                        <SwitchRoleMenu
                            availableRoles={availableRoles}
                            currentActiveRole={currentActiveRole}
                            onRoleSelect={(roleVal) =>
                                handleRoleSwitch(roleVal, userInfo, enterpriseId, history, availableRoles)
                            }
                        />
                    ) : null}
                    {showEnterpriseSelect ? (
                        <EnterpriseSelect
                            mappedEnterprise={mappedEnterprise}
                            enterpriseId={enterpriseId}
                            getMappedEnterprises={getMappedEnterprises}
                            userInfo={userInfo}
                        />
                    ) : null}
                </RightButtons>
            }
            onClick={onNavClick}
            checkIsActive={checkIsActive}
        />
    );
}

/** Route helpers below retain legacy enterprise/role switch side effects (grid refresh, CTMS date, Redux). */

const adminRoute = (dispatch, history, entId) => {
    dispatch(trackUser(entId));
    dispatch(setSafetyNotifications());
    history.push('/safetyAdmin/notifications');
    dispatch(setCurrentEnterprise(entId));
    dispatch(refreshManagement('safetyAdminGrid', () => reset('safetyAdminGrid_advancedSearch')));
    dispatch(getEnterpriseCtmsRefreshDate(entId)).then((response) =>
        dispatch(setCtmsRefreshDate(response))
    );
};

const gapPacketRoute = (dispatch, history, entId) => {
    dispatch(trackUser(entId));
    history.push('/gapPacketManagement/gapPacketList');
    dispatch(setCurrentEnterprise(entId));
    dispatch(refreshManagement('GapPacketResultsGrid', () => reset('gapPacketForm')));
    dispatch(getEnterpriseCtmsRefreshDate(entId)).then((response) =>
        dispatch(setCtmsRefreshDate(response))
    );
};

const iBRoute = (dispatch, history, entId) => {
    dispatch(trackUser(entId));
    dispatch(setSafetyNotifications());
    history.push('/ibAdmin/notifications');
    dispatch(setCurrentEnterprise(entId));
    dispatch(refreshManagement('IBAdminGrid', () => reset('IBAdminGrid_advancedSearch')));
    dispatch(getEnterpriseCtmsRefreshDate(entId)).then((response) =>
        dispatch(setCtmsRefreshDate(response))
    );
};

const safetyRecipientRoute = (dispatch, history, entId) => {
    dispatch(setSafetyNotifications());
    history.push('/safetyNotifications/notifications');
    dispatch(setCurrentEnterprise(entId));
    resetFilters(dispatch, 'safetyletterRecipient');
};

const gapPacketRecipientRoute = (dispatch, history, entId) => {
    history.push('/gapPacketManagement/acknowledge');
    dispatch(setCurrentEnterprise(entId));
    dispatch(refreshRecipient('GapPacketRecipientResultsGrid', () => reset('gapPacketForm')));
};

const ibRecipientRoute = (dispatch, history, entId) => {
    history.push('/ibNotifications/notifications');
    dispatch(setCurrentEnterprise(entId));
    dispatch(refreshRecipient('ibRecipient', () => reset('IBRecipientGridForm')));
};

const resetFilters = (dispatch, gridName) => {
    // formName arg comes from refreshRecipient callback (grid advanced-search form), not EnterpriseListForm.
    dispatch(refreshRecipient(gridName, (formName) => change(formName, 'status', '')));
    dispatch(refreshRecipient(gridName, (formName) => change(formName, 'distDue', '')));
    dispatch(refreshRecipient(gridName, (formName) => change(formName, 'searchParam', '')));
    dispatch(refreshRecipient(gridName, (formName) => change(formName, 'sentDue', '')));
};

const setRolesForEnterprise = (mappedEnterprise, entId, dispatch, userInfo) => {
    const selectedEnterpriseData =
        mappedEnterprise && mappedEnterprise.filter((value) => value.entId === entId);
    dispatch(
        setDomainValue(
            selectedEnterpriseData[0].domain,
            userInfo,
            selectedEnterpriseData[0].isEntAutoEnabled
        )
    );
    const roles = selectedEnterpriseData[0].roles;
    const rolesList = roles && roles.map((role) => role.roleVal);
    // Default to first role for the enterprise when switching enterprises (legacy behavior).
    return rolesList && rolesList.length > 0 ? rolesList[0] : '';
};

// SAF-20237: role order and route precedence preserved from legacy SparkHeader (PSR-4763).
const executeNavigation = (dispatch, history, selectedRole, enterpriseId, userInfo, allRoles) => {
    const managementRoles = [
        Roles.SAFETY_ADMIN,
        Roles.SAFETY_MANAGER,
        Roles.SAFETY_MONITOR,
        Roles.COUNTRY_LEAD,
        Roles.SAFETY_SUPPORT,
        Roles.GAP_PACKET_ADMIN,
        Roles.IB_ADMIN,
        Roles.IB_COUNTRY_LEAD,
        Roles.IB_MONITOR,
        Roles.IB_SUPPORT,
        Roles.GAP_PACKET_SUPPORT,
    ];
    const gapPacketRoles = [Roles.GAP_PACKET_ADMIN, Roles.GAP_PACKET_SUPPORT];
    const gapPacketRecipientRoles = [Roles.GP_RECP];
    const iBRoles = [Roles.IB_ADMIN, Roles.IB_COUNTRY_LEAD, Roles.IB_MONITOR, Roles.IB_SUPPORT];
    const ibRecipientRoles = [Roles.IB_RECP];

    dispatch(setUserInfo(userInfo, selectedRole));

    const selectedRoleData = allRoles && allRoles.find((r) => r.roleVal === selectedRole);
    const reportEnabledSponsor =
        selectedRoleData && selectedRoleData.reportEnabledForSponsor ? true : false;
    const reportEnabledCRO = selectedRoleData && selectedRoleData.reportEnabledForCRO ? true : false;

    dispatch(setReportEnabledForSponsor(reportEnabledSponsor));
    dispatch(setReportEnabledForCRO(reportEnabledCRO));
    dispatch(setCurrentEnterpriseRole(managementRoles.includes(selectedRole) ? 'MNGT' : 'RECP'));

    const isIBRole = iBRoles.includes(selectedRole);
    const isGapPacketRole = gapPacketRoles.includes(selectedRole);
    const isGapPacketRecipientRole = gapPacketRecipientRoles.includes(selectedRole);
    const isIBRecipientRole = ibRecipientRoles.includes(selectedRole);

    if (isIBRole) {
        iBRoute(dispatch, history, enterpriseId);
    } else if (isIBRecipientRole) {
        ibRecipientRoute(dispatch, history, enterpriseId);
    } else if (isGapPacketRole) {
        gapPacketRoute(dispatch, history, enterpriseId);
    } else if (isGapPacketRecipientRole) {
        gapPacketRecipientRoute(dispatch, history, enterpriseId);
    } else if (managementRoles.includes(selectedRole)) {
        adminRoute(dispatch, history, enterpriseId);
    } else {
        safetyRecipientRoute(dispatch, history, enterpriseId);
    }
};

export default connect(
    (state) => {
        const enterpriseId = userCurrentEnterprise(state);
        const mappedEnterprise = getMappedEnterpriseData(state);
        const selectedEnterprise =
            mappedEnterprise &&
            mappedEnterprise.find((ent) => ent.entId === parseInt(enterpriseId, 10));
        const availableRoles = (selectedEnterprise && selectedEnterprise.roles) || [];

        return {
            mappedEnterprise,
            isManagementUser: isManagementUser(state),
            isRecipient: isRecipient(state),
            isGapPacketAdmin: isGapPacketAdmin(state),
            isIBManagementUser: isIBManagementUser(state),
            userInfo: currentUserInfo(state),
            isSupportUser: isSupportUser(state),
            enterpriseId,
            availableRoles,
            currentActiveRole: getCurrentActiveRole(state),
            reportEnabled: getReportEnabledForSponsor(state) || getReportEnabledForCRO(state),
        };
    },
    (dispatch) => ({
        logOut,
        // Enterprise change: pick default role for new enterprise, then navigate (see executeNavigation).
        getMappedEnterprises: (entId, history, userInfo, mappedEnterprise) => {
            const selectedEnterpriseData =
                mappedEnterprise && mappedEnterprise.filter((value) => value.entId === entId);
            const availableRoles = selectedEnterpriseData[0].roles || [];
            const selectedEntRole = setRolesForEnterprise(
                mappedEnterprise,
                entId,
                dispatch,
                userInfo
            );
            executeNavigation(dispatch, history, selectedEntRole, entId, userInfo, availableRoles);
        },
        handleRoleSwitch: (selectedRole, userInfo, enterpriseId, history, availableRoles) => {
            executeNavigation(
                dispatch,
                history,
                selectedRole,
                enterpriseId,
                userInfo,
                availableRoles
            );
        },
        resetReportType: () => dispatch(change('safetyReportForm', 'reportType', '')),
    })
)(SparkHeader);
